import { useCallback, useEffect, useRef } from 'react';
import ReactModal from 'react-modal';
import type { ComponentType } from 'react';
import { Box, Flex, Heading } from '@radix-ui/themes';
import { IconX } from '@tabler/icons-react';
import { useTenantContext } from '@trashlab/sitesapp/src/contexts/use-tenant-context';
import { DumpsterTypes } from '@trashlab/sitesapp/src/templates/default/components/dumpster-rentals/dumpster-types';
import { useVariantValue } from '@trashlab/sitesapp/src/lib/utils/get-variant-value';
import { ServiceOptions } from '@trashlab/sitesapp/src/templates/default/components/home/service-options';

const ReactModalCompat = ReactModal as unknown as ComponentType<Record<string, unknown>>;

// Ensure react-modal is properly set up for accessibility
if (typeof window !== 'undefined') {
  try {
    // Try to set app element, falling back through options if needed
    try {
      ReactModal.setAppElement('#__next'); // Next.js
    } catch (e) {
      try {
        ReactModal.setAppElement('#root'); // Create React App
      } catch (e) {
        ReactModal.setAppElement('body'); // Fallback
      }
    }
  } catch (e) {
    // If all attempts fail, log a warning
    console.warn('Could not set app element for ReactModal', e);
  }
}

interface DumpsterModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultType?: string;
}

export const DumpsterModal = ({
  isOpen,
  onClose,
  defaultType = 'all',
}: DumpsterModalProps) => {
  const { website } = useTenantContext();
  const contentRef = useRef<HTMLDivElement>(null);

  // Get theme colors from the same source as the main app
  const primaryColor = useVariantValue('THEME_PRIMARY_COLOR', '#3463F8');
  const secondaryColor = useVariantValue('THEME_SECONDARY_COLOR', '#5460C3');
  const backgroundColor = useVariantValue('THEME_BACKGROUND_COLOR', '#001B5E');
  const backgroundColorLight = useVariantValue(
    'THEME_BACKGROUND_COLOR_LIGHT',
    '#EFF3FE',
  );

  const modalOverlayStyles = {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(4px)',
    padding: '1rem',
    transition: 'all 300ms ease-in-out',
    overflowY: 'auto' as const,
    WebkitOverflowScrolling: 'touch' as const,
  };

  const modalContentStyles = {
    position: 'relative' as const,
    width: '100%',
    maxWidth: '80vw',
    maxHeight: '90vh',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    padding: 0,
    margin: '0 auto',
    inset: 'auto',
    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    overflowY: 'auto' as const,
  };

  // Handle escape key press
  const handleEscapeKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  // Handle click outside
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        contentRef.current &&
        !contentRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    },
    [onClose],
  );

  // Add event listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden'; // Prevent scrolling when modal is open
    }
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = ''; // Re-enable scrolling when modal is closed
    };
  }, [isOpen, handleEscapeKey, handleClickOutside]);

  return (
    <ReactModalCompat
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Service Options"
      style={{
        overlay: modalOverlayStyles,
        content: modalContentStyles,
      }}
      closeTimeoutMS={300}
      className={{
        base: 'transform transition-all duration-300 ease-out scale-95 opacity-0',
        afterOpen: 'scale-100 opacity-100',
        beforeClose: 'scale-95 opacity-0',
      }}
      overlayClassName={{
        base: 'opacity-0 transition-all duration-300 ease-out backdrop-blur-none',
        afterOpen: 'opacity-100 backdrop-blur-sm',
        beforeClose: 'opacity-0 backdrop-blur-none',
      }}
      portalClassName="dumpster-modal-portal z-[9999]"
      shouldCloseOnOverlayClick={true}
      shouldReturnFocusAfterClose={true}
      preventScroll={true}
    >
      <style>
        {`
          :root {
            --primary-color: ${primaryColor};
            --secondary-color: ${secondaryColor};
            --background-color: ${backgroundColor};
            --background-color-light: ${backgroundColorLight};
            --success-color: #40C057;
            --danger-color: #FA5252;
          }
          
          .dumpster-modal-portal {
            z-index: 9999;
          }
          
          /* Ensure proper modal content styles */
          .dumpster-modal-portal .ReactModal__Content {
            max-width: 1200px;
            width: calc(100% - 32px);
            max-height: 90vh;
            overflow-y: auto;
            margin: 0 auto;
          }
          
          /* Service options styling */
          .service-options-container {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
            gap: 24px;
            padding: 16px;
          }
          
          @media (min-width: 768px) {
            .service-options-container {
              grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            }
          }
          
          /* Add responsive padding */
          @media (max-width: 768px) {
            .dumpster-modal-portal .ReactModal__Content {
              padding: 0;
              width: calc(100% - 16px);
            }
          }
        `}
      </style>
      <Box
        ref={contentRef}
        className="bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        <Flex justify="between" align="center" p="4" className="border-b">
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close modal"
          >
            <IconX size={24} />
          </button>
        </Flex>

        <Box className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
          <ServiceOptions />
        </Box>
      </Box>
    </ReactModalCompat>
  );
};

export default DumpsterModal; 